import fs from "node:fs";
import path from "node:path";
import { createRequire } from "node:module";
import { configDir } from "./config";
import { buildTarget } from "../lib/build-info";

/**
 * Secret storage for profile credentials.
 *
 * Prefers the OS-native keychain (macOS Keychain, Windows Credential Manager,
 * libsecret on Linux) via `@napi-rs/keyring`, and falls back to a 0600
 * `credentials.json` next to the config file when the native addon can't load
 * (Linux without libsecret, musl, compiled binaries). The keyring is loaded
 * lazily so an unloadable addon degrades to the file store instead of crashing
 * the CLI at startup. Force a store with `WHOP_CLI_SECRET_STORE=file|keyring`.
 *
 * `storesFor` resolves an ordered list of candidate stores: reads take the
 * first hit, writes go to the first store that accepts the secret and remove
 * the entry from the rest, so a fallback flip can never resurface a stale
 * secret. Deletes clear every candidate store.
 */

const SERVICE = "whop-cli";

type KeyringModule = typeof import("@napi-rs/keyring");

let keyringModule: KeyringModule | null | undefined;

function loadKeyring(): KeyringModule | null {
	if (keyringModule === undefined) {
		try {
			const require = createRequire(import.meta.url);
			keyringModule = require("@napi-rs/keyring") as KeyringModule;
		} catch {
			keyringModule = null;
		}
	}
	return keyringModule;
}

interface SecretStore {
	/** Returns false when the store is unavailable, so the next candidate is tried. */
	set(name: string, secret: string): boolean;
	get(name: string): string | null;
	delete(name: string): void;
}

const keyringStore: SecretStore = {
	set(name, secret) {
		const mod = loadKeyring();
		if (!mod) return false;
		try {
			new mod.Entry(SERVICE, name).setPassword(secret);
			return true;
		} catch {
			return false;
		}
	},
	get(name) {
		const mod = loadKeyring();
		if (!mod) return null;
		try {
			return new mod.Entry(SERVICE, name).getPassword();
		} catch {
			return null;
		}
	},
	delete(name) {
		const mod = loadKeyring();
		if (!mod) return;
		try {
			new mod.Entry(SERVICE, name).deletePassword();
		} catch {
			// nothing to delete (or keychain unavailable) — treat as already gone
		}
	},
};

function credentialsPath(env: NodeJS.ProcessEnv): string {
	return path.join(configDir(env), "credentials.json");
}

function readCredentials(env: NodeJS.ProcessEnv): Record<string, string> {
	let raw: string;
	try {
		raw = fs.readFileSync(credentialsPath(env), "utf8");
	} catch {
		return {};
	}
	try {
		const parsed = JSON.parse(raw) as { secrets?: unknown };
		if (
			parsed != null &&
			typeof parsed === "object" &&
			parsed.secrets != null &&
			typeof parsed.secrets === "object" &&
			!Array.isArray(parsed.secrets)
		) {
			const secrets: Record<string, string> = {};
			for (const [key, value] of Object.entries(parsed.secrets)) {
				if (typeof value === "string") secrets[key] = value;
			}
			return secrets;
		}
	} catch {
		// corrupt file — treat as empty rather than bricking the CLI
	}
	return {};
}

function writeCredentials(
	secrets: Record<string, string>,
	env: NodeJS.ProcessEnv,
): void {
	const dir = configDir(env);
	fs.mkdirSync(dir, { recursive: true, mode: 0o700 });
	const file = credentialsPath(env);
	const tmp = `${file}.${process.pid}.tmp`;
	fs.writeFileSync(
		tmp,
		`${JSON.stringify({ version: 1, secrets }, null, 2)}\n`,
		{ mode: 0o600 },
	);
	fs.renameSync(tmp, file);
	try {
		fs.chmodSync(file, 0o600);
	} catch {
		// best effort — chmod is a no-op on some platforms (e.g. Windows)
	}
}

function fileStore(env: NodeJS.ProcessEnv): SecretStore {
	return {
		set(name, secret) {
			const secrets = readCredentials(env);
			secrets[name] = secret;
			writeCredentials(secrets, env);
			return true;
		},
		get(name) {
			return readCredentials(env)[name] ?? null;
		},
		delete(name) {
			// Rewrite whenever the file exists (not just when the entry parses):
			// a corrupt credentials.json still holds secret bytes, and delete must
			// clear them even though reads already treat the file as empty.
			if (!fs.existsSync(credentialsPath(env))) return;
			const secrets = readCredentials(env);
			delete secrets[name];
			writeCredentials(secrets, env);
		},
	};
}

function storesFor(env: NodeJS.ProcessEnv): SecretStore[] {
	const forced = env.WHOP_CLI_SECRET_STORE?.trim();
	if (forced === "file") return [fileStore(env)];
	if (forced === "keyring") return [keyringStore];
	// Standalone binaries default to the file store: self-update replaces the
	// executable, which changes its code signature and would make macOS re-prompt
	// for keychain access on every update.
	if (buildTarget) return [fileStore(env)];
	return [keyringStore, fileStore(env)];
}

export function setSecret(
	name: string,
	secret: string,
	env: NodeJS.ProcessEnv = process.env,
): void {
	const stores = storesFor(env);
	const winner = stores.find((store) => store.set(name, secret));
	if (!winner) {
		throw new Error(
			"WHOP_CLI_SECRET_STORE=keyring but the OS keychain is unavailable",
		);
	}
	for (const store of stores) {
		if (store !== winner) store.delete(name);
	}
}

export function getSecret(
	name: string,
	env: NodeJS.ProcessEnv = process.env,
): string | null {
	for (const store of storesFor(env)) {
		const secret = store.get(name);
		if (secret != null) return secret;
	}
	return null;
}

export function deleteSecret(
	name: string,
	env: NodeJS.ProcessEnv = process.env,
): void {
	for (const store of storesFor(env)) {
		store.delete(name);
	}
}
